import { useState, useEffect, useMemo } from 'react';
import { AlertTriangle, TrendingDown, BarChart3 } from 'lucide-react';
import { useCrisisScenarios, useScenarioImpacts, useScenarioResults } from '../../hooks/useCrisisScenarios';
import { computeScenario, compareScenarios, type TreasuryLine, type Impact, type ScenarioParameters } from '../../utils/crisisScenarioEngine';
import { CreateScenarioModal } from './CreateScenarioModal';
import { CrisisScenariosList } from './CrisisScenariosList';
import { ScenarioComparison } from './ScenarioComparison';
import { supabase, getUserId } from '../../lib/supabase';

interface CrisisScenarioManagerProps {
  onIndicatorsChange?: (indicators: any) => void;
}

export function CrisisScenarioManager({ onIndicatorsChange }: CrisisScenarioManagerProps) {
  const {
    scenarios,
    activeScenario,
    loading: scenariosLoading,
    createScenario,
    deleteScenario,
    setActive
  } = useCrisisScenarios();

  const {
    impacts,
    loading: impactsLoading,
    addImpact
  } = useScenarioImpacts(activeScenario?.id || null);

  const {
    result,
    saveResult
  } = useScenarioResults(activeScenario?.id || null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [treasuryLines, setTreasuryLines] = useState<TreasuryLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'base' | 'scenario' | 'comparison'>('base');

  const today = new Date();
  const sixMonthsFromNow = new Date();
  sixMonthsFromNow.setMonth(sixMonthsFromNow.getMonth() + 6);

  const parameters: ScenarioParameters = {
    months_security: 3,
    rupture_mode: 'security',
    initial_treasury: 50000
  };

  useEffect(() => {
    loadTreasuryLines();
  }, []);

  useEffect(() => {
    if (activeScenario && impacts.length > 0 && treasuryLines.length > 0) {
      computeAndSaveScenario();
    }
  }, [activeScenario, impacts, treasuryLines]);

  useEffect(() => {
    if (activeScenario) {
      setViewMode('scenario');
    } else {
      setViewMode('base');
    }
  }, [activeScenario]);

  const loadTreasuryLines = async () => {
    try {
      setLoading(true);
      const userId = await getUserId();

      const { data: transactions, error: transactionsError } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', userId)
        .gte('date', today.toISOString().split('T')[0])
        .lte('date', sixMonthsFromNow.toISOString().split('T')[0])
        .order('date', { ascending: true });

      if (transactionsError) throw transactionsError;

      const { data: budgetLines, error: budgetError } = await supabase
        .from('cash_flow_forecast_lines')
        .select('id, forecast_year, forecast_month, amount, description, type')
        .eq('user_id', userId)
        .order('forecast_year', { ascending: true })
        .order('forecast_month', { ascending: true });

      if (budgetError) throw budgetError;

      const transactionLines: TreasuryLine[] = (transactions || []).map(t => ({
        id: t.id,
        date: new Date(t.date),
        amount: t.amount,
        label: t.label,
        is_fixed_charge: t.is_fixed_charge || false
      }));

      const forecastLines: TreasuryLine[] = (budgetLines || [])
        .map(b => {
          const lineDate = new Date(b.forecast_year, b.forecast_month - 1, 15);
          return {
            id: b.id,
            date: lineDate,
            amount: b.amount,
            label: b.description || `${b.type} - ${b.forecast_month}/${b.forecast_year}`,
            is_fixed_charge: false
          };
        })
        .filter(line =>
          line.date >= today && line.date <= sixMonthsFromNow
        );

      const allLines = [...transactionLines, ...forecastLines].sort((a, b) =>
        a.date.getTime() - b.date.getTime()
      );

      setTreasuryLines(allLines);
    } catch (err) {
      console.error('Error loading treasury lines:', err);
    } finally {
      setLoading(false);
    }
  };

  const baseResult = useMemo(() => {
    if (treasuryLines.length === 0) return null;
    return computeScenario(treasuryLines, [], parameters);
  }, [treasuryLines]);

  const scenarioResult = useMemo(() => {
    if (!activeScenario || treasuryLines.length === 0 || impacts.length === 0) return null;

    const mappedImpacts: Impact[] = impacts.map(impact => ({
      id: impact.id,
      type: impact.type,
      target_line_id: impact.target_line_id,
      amount: impact.amount,
      percentage: impact.percentage,
      delay_days: impact.delay_days,
      spread_periods: impact.spread_periods,
      label: impact.label
    }));

    return computeScenario(treasuryLines, mappedImpacts, parameters);
  }, [activeScenario, treasuryLines, impacts]);

  const computeAndSaveScenario = async () => {
    if (!activeScenario || !scenarioResult) return;

    await saveResult(activeScenario.id, {
      treasury_min: scenarioResult.treasury_min,
      treasury_min_date: scenarioResult.treasury_min_date.toISOString().split('T')[0],
      security_threshold: scenarioResult.security_threshold,
      frd: scenarioResult.frd,
      rupture_date: scenarioResult.rupture_date?.toISOString().split('T')[0],
      runway_days: scenarioResult.runway_days,
      fixed_charges_monthly: scenarioResult.fixed_charges_monthly
    });

    if (onIndicatorsChange) {
      onIndicatorsChange({
        treasury_min: scenarioResult.treasury_min,
        frd: scenarioResult.frd,
        rupture_date: scenarioResult.rupture_date,
        runway_days: scenarioResult.runway_days
      });
    }
  };

  const handleCreateScenario = async (
    name: string,
    description: string,
    impactType: string,
    impactData: any
  ) => {
    const scenarioId = await createScenario(
      name,
      description,
      today.toISOString().split('T')[0],
      sixMonthsFromNow.toISOString().split('T')[0]
    );

    if (scenarioId) {
      let internalType: 'SHIFT' | 'SCALE' | 'ADD' | 'SPREAD' = 'ADD';
      let finalImpactData: any = {};

      switch (impactType) {
        case 'DELAY':
          internalType = 'SHIFT';
          finalImpactData = {
            target_line_id: impactData.target_line_id,
            delay_days: impactData.delay_days,
            label: `Retard de ${impactData.delay_days} jours`
          };
          break;
        case 'LOSS':
          internalType = 'SCALE';
          finalImpactData = {
            target_line_id: impactData.target_line_id,
            percentage: 0,
            label: 'Perte totale'
          };
          break;
        case 'PARTIAL':
          internalType = 'SCALE';
          finalImpactData = {
            target_line_id: impactData.target_line_id,
            percentage: impactData.percentage,
            label: `Encaissement partiel (${Math.round(impactData.percentage * 100)}%)`
          };
          break;
        case 'UNEXPECTED':
          internalType = 'ADD';
          finalImpactData = {
            amount: impactData.amount,
            label: 'Décaissement imprévu'
          };
          break;
        case 'OVERCOST':
          internalType = 'SCALE';
          finalImpactData = {
            target_line_id: impactData.target_line_id,
            percentage: impactData.percentage,
            label: `Surcoût (${Math.round((impactData.percentage - 1) * 100)}%)`
          };
          break;
        case 'SPREAD':
          internalType = 'SPREAD';
          finalImpactData = {
            amount: impactData.amount,
            spread_periods: impactData.spread_periods,
            label: `Étalement sur ${impactData.spread_periods} mois`
          };
          break;
      }

      await addImpact({
        scenario_id: scenarioId,
        type: internalType,
        ...finalImpactData
      });

      await setActive(scenarioId);
    }

    setShowCreateModal(false);
  };

  const handleSelectScenario = async (scenarioId: string | null) => {
    await setActive(scenarioId);
  };

  const handleDeleteScenario = async (scenarioId: string) => {
    await deleteScenario(scenarioId);
  };

  const comparisonData = useMemo(() => {
    if (!baseResult || !scenarioResult) return null;

    return {
      base: {
        treasury_min: baseResult.treasury_min,
        frd: baseResult.frd,
        rupture_date: baseResult.rupture_date,
        runway_days: baseResult.runway_days,
        security_threshold: baseResult.security_threshold
      },
      scenario: {
        treasury_min: scenarioResult.treasury_min,
        frd: scenarioResult.frd,
        rupture_date: scenarioResult.rupture_date,
        runway_days: scenarioResult.runway_days,
        security_threshold: scenarioResult.security_threshold
      }
    };
  }, [baseResult, scenarioResult]);

  if (loading || scenariosLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-lg p-6">
        <div className="flex items-start space-x-4">
          <AlertTriangle className="h-8 w-8 flex-shrink-0" />
          <div>
            <h2 className="text-2xl font-bold mb-2">Crash Tests - Scénarios de Crise</h2>
            <p className="text-red-50">
              Simulez des événements critiques et mesurez leur impact sur votre trésorerie.
              Anticipez les crises avant qu'elles ne surviennent.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-4 bg-white rounded-lg border border-gray-200 p-4">
        <button
          onClick={() => setViewMode('base')}
          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
            viewMode === 'base'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Situation de base
        </button>
        <button
          onClick={() => setViewMode('scenario')}
          disabled={!activeScenario}
          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
            viewMode === 'scenario'
              ? 'bg-red-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
        >
          Scénario actif
        </button>
        <button
          onClick={() => setViewMode('comparison')}
          disabled={!activeScenario}
          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
            viewMode === 'comparison'
              ? 'bg-purple-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed'
          }`}
        >
          Comparaison
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <CrisisScenariosList
            scenarios={scenarios}
            activeScenarioId={activeScenario?.id || null}
            onSelectScenario={handleSelectScenario}
            onDeleteScenario={handleDeleteScenario}
            onCreateScenario={() => setShowCreateModal(true)}
          />
        </div>

        <div className="lg:col-span-2">
          {viewMode === 'comparison' && comparisonData && activeScenario && (
            <ScenarioComparison
              data={comparisonData}
              scenarioName={activeScenario.name}
            />
          )}

          {viewMode === 'base' && baseResult && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Indicateurs de base</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Trésorerie min</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(baseResult.treasury_min)}
                  </div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">FRD</div>
                  <div className={`text-2xl font-bold ${baseResult.frd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(baseResult.frd)}
                  </div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Date de rupture</div>
                  <div className="text-lg font-bold text-gray-900">
                    {baseResult.rupture_date ? new Date(baseResult.rupture_date).toLocaleDateString('fr-FR') : 'Aucune'}
                  </div>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Runway</div>
                  <div className="text-lg font-bold text-gray-900">
                    {baseResult.runway_days !== null ? `${baseResult.runway_days} jours` : 'Infini'}
                  </div>
                </div>
              </div>
            </div>
          )}

          {viewMode === 'scenario' && scenarioResult && activeScenario && (
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {activeScenario.name}
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Trésorerie min</div>
                  <div className="text-2xl font-bold text-gray-900">
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(scenarioResult.treasury_min)}
                  </div>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">FRD</div>
                  <div className={`text-2xl font-bold ${scenarioResult.frd >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 }).format(scenarioResult.frd)}
                  </div>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Date de rupture</div>
                  <div className="text-lg font-bold text-gray-900">
                    {scenarioResult.rupture_date ? new Date(scenarioResult.rupture_date).toLocaleDateString('fr-FR') : 'Aucune'}
                  </div>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <div className="text-sm text-gray-600 mb-1">Runway</div>
                  <div className="text-lg font-bold text-gray-900">
                    {scenarioResult.runway_days !== null ? `${scenarioResult.runway_days} jours` : 'Infini'}
                  </div>
                </div>
              </div>

              {impacts.length > 0 && (
                <div className="mt-6">
                  <h4 className="font-medium text-gray-900 mb-3">Impacts appliqués</h4>
                  <div className="space-y-2">
                    {impacts.map(impact => (
                      <div key={impact.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-900">{impact.label}</span>
                          <span className="text-xs text-gray-500">{impact.type}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!activeScenario && viewMode !== 'base' && (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
              <TrendingDown className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun scénario sélectionné</h3>
              <p className="text-gray-500">
                Créez un crash test pour simuler une crise financière
              </p>
            </div>
          )}
        </div>
      </div>

      {showCreateModal && (
        <CreateScenarioModal
          onClose={() => setShowCreateModal(false)}
          onCreateScenario={handleCreateScenario}
          treasuryLines={treasuryLines.map(line => ({
            id: line.id,
            date: line.date,
            label: line.label,
            amount: line.amount
          }))}
        />
      )}
    </div>
  );
}
